import store from '@/store';

/**
 * Get formatted storage usage data
 * @returns {Object} Object with storage metrics
 */
export function getStorageMetrics() {
  return {
    maxStorage: store.getters['resources/formattedMaxStorage'],
    usedStorage: store.getters['resources/formattedUsedStorage'],
    percentage: store.getters['resources/storagePercentage'],
    color: store.getters['resources/storageBarColor']
  };
}

/**
 * Get formatted team members usage data
 * @returns {Object} Object with team member metrics
 */
export function getTeamMetrics() {
  return {
    maxMembers: store.getters['resources/maxMembers'],
    totalMembers: store.getters['resources/totalMembers'],
    memberCount: store.getters['resources/memberCount'],
    percentage: store.getters['resources/memberPercentage'],
    color: store.getters['resources/memberBarColor']
  };
}

/**
 * Check if adding storage would exceed plan limits
 * @param {number} additionalBytes - Number of bytes to potentially add
 * @returns {Object} Object with validation results
 */
export function checkStorageLimit(additionalBytes = 0) {
  const maxBytes = store.getters['resources/maxStorageBytes'];
  const usedBytes = store.getters['resources/usedStorageBytes'];

  // Handle unlimited storage
  if (!maxBytes || maxBytes === 0) {
    return { allowed: true };
  }

  // Calculate if adding bytes would exceed storage limit
  const remainingBytes = Math.max(0, maxBytes - usedBytes);
  const isExceeded = additionalBytes > remainingBytes;

  // Return only basic information when allowed
  if (!isExceeded) {
    return { allowed: true };
  }

  // Format values for display in error messages when not allowed
  const formatBytes = bytes => store.getters['resources/formattedBytes'](bytes);

  // Return detailed information for error messages
  return {
    allowed: false,
    maxStorage: formatBytes(maxBytes),
    usedStorage: formatBytes(usedBytes),
    remainingStorage: formatBytes(remainingBytes),
    uploadSize: formatBytes(additionalBytes),
    exceededBy: formatBytes(additionalBytes - remainingBytes)
  };
}

/**
 * Check if adding a team member would exceed plan limits
 * @returns {Object} Object with validation results
 */
export function checkTeamMemberLimit() {
  const maxMembers = store.getters['resources/maxMembers'];
  const totalMembers = store.getters['resources/totalMembers'];

  // Handle unlimited members
  if (maxMembers === null || maxMembers === 0) {
    return { allowed: true };
  }

  // Calculate if adding a member would exceed the limit
  const remaining = Math.max(0, maxMembers - totalMembers);
  const allowed = totalMembers < maxMembers;

  return {
    allowed,
    maxMembers,
    currentMembers: totalMembers,
    remaining
  };
}

export default {
  getStorageMetrics,
  getTeamMetrics,
  checkStorageLimit,
  checkTeamMemberLimit
};
